import { Facility } from '@reliance/types/models/Facility';
import { ConfigurationText } from '@reliance/types/models/ConfigurationText';
import React, { useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { alertStore } from '../alert/alertStore';
import { useConfigurationTexts } from '../hooks/ConfigurationText';
import { useNotifyTabLoading } from '../hooks/useTabLoadingReporter';
import AddEditModal from '../configuration-text/AddEditModal';
import ConfigurationTextList from '../configuration-text/List';
import { LANGUAGE_OPTIONS } from '../configuration-text/languageOptions';

type FacilityConfigurationTextsTabProps = {
  facility: Facility;
};

const FacilityConfigurationTextsTab: React.FC<FacilityConfigurationTextsTabProps> = ({ facility }) => {
  const [selectedLanguage, setSelectedLanguage] = useState('EN-US');
  const [modalRow, setModalRow] = useState<ConfigurationText | null | undefined>(undefined);

  const listResult = useConfigurationTexts(facility.id, selectedLanguage);

  useNotifyTabLoading('configuration-texts', listResult.loading);

  useEffect(() => {
    if (listResult.error) {
      alertStore.error('An error occurred while loading configuration texts');
    }
  }, [listResult.error]);

  const rows = useMemo(() => {
    const data = listResult.result ?? [];
    return [...data].sort((a, b) => String(a.key).localeCompare(String(b.key)));
  }, [listResult.result]);

  const closeModal = (success?: boolean) => {
    setModalRow(undefined);
    if (success) {
      void listResult.execute(facility.id, selectedLanguage);
    }
  };

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div className="content">
        <Row className="mb-3">
          <Col md={3}>
            <Button variant="primary" className="btn-labeled" onClick={() => setModalRow(null)}>
              <span className="btn-label">
                <i className="fas fa-plus-circle" />
              </span>
              Add Configuration Text
            </Button>
          </Col>
          <Col md={3} className="ms-md-auto">
            <Form.Label className="me-2">Language</Form.Label>
            <Form.Select
              className="d-inline-block w-auto"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}>
              {LANGUAGE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.name}
                </option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <ConfigurationTextList rows={rows} loading={listResult.loading} onEdit={(row) => setModalRow(row)} />
          </Col>
        </Row>
      </div>
      {modalRow !== undefined && (
        <AddEditModal facilityId={facility.id} configurationText={modalRow ?? undefined} onClose={closeModal} />
      )}
    </div>
  );
};

export default FacilityConfigurationTextsTab;
